import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { SelectAllButton, SelectionContainer } from '@/components/itemSelection/ItemSelection';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import SiGrid from '@/icons/SiGrid';
import SiPlus from '@/icons/SiPlus';
import SiUpload from '@/icons/SiUpload';
import { cn } from '@/lib/utils';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { useExplorer, useItem, useItemTree, useSize } from '@/services/explorer/useExplorerProvider';
import { useUploadFiles } from '@/services/upload/hooks';
import { useInvalidateList } from '@/utils/explorer';
import { useChildren, useCreateFolder, useInvalidateChildren } from '@/utils/folder';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, ReactNode, Suspense, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { DropContainer } from './DropContainer';
import { Folder, FolderList } from './Folder';
import { Resource, ResourceList } from './Resource';
import { ResourceThumbnail, Thumbnail } from './Thumbnail';
import { sortItem } from './helper';

interface DirectoryProps {
  directoryId: string;
  itemId: string;
}

export const DirectoryPlaceholder: FC<PropsWithChildren<DirectoryProps & { actions?: ReactNode }>> = ({
  directoryId,
  itemId,
  children,
  actions
}) => {
  const item = useItem(directoryId, itemId);
  const tree = useItemTree(directoryId, itemId);

  if (!item || !tree) throw new Error('Placeholder: Missing item or tree');

  const originalItems = tree?.items?.sort(sortItem);
  const folders = originalItems?.filter(child => child.type === 'folder');
  const assets = originalItems?.filter(child => child.type === 'file');

  const TemporaryFolder: FC<{ id: string }> = ({ id }) => {
    const item = useItem(directoryId, id);
    const tree = useItemTree(directoryId, id);

    return <Folder directoryId={directoryId} id={id} name={item.name} count={tree?.items?.length ?? 0} />;
  };

  const TemporaryAsset: FC<{ id: string }> = ({ id }) => {
    const item = useItem(directoryId, id);

    return (
      <SelectionContainer key={id} id={id}>
        <div className={cn('rounded-md p-1')}>
          <Resource name={item.name} id={id}>
            <div className="mx-auto aspect-[6/4] max-h-full w-full rounded-lg bg-gray-50"></div>
          </Resource>
        </div>
      </SelectionContainer>
    );
  };

  const hasFolders = folders && folders.length > 0;
  const hasAssets = assets && assets.length > 0;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex px-5 justify-between bg-white bg-opacity-80 pb-5 border-b">
        {children}
        <div className="flex gap-3">{actions}</div>
      </div>

      <div className="flex w-full flex-col gap-2">
        {!hasFolders && !hasAssets && (
          <Card className="py-5 px-10 flex flex-col items-center gap-5 w-2/3 mx-auto max-w-4xl text-center">
            <p className="font-semibold">Welcome to the GTM Media area!</p>
          </Card>
        )}
        {hasFolders && (
          <FolderList>
            {folders.map(child => (
              <TemporaryFolder key={child.id} id={child.id} />
            ))}
          </FolderList>
        )}
        {hasAssets && (
          <ResourceList>
            {assets.map(child => (
              <TemporaryAsset key={child.id} id={child.id} />
            ))}
          </ResourceList>
        )}
      </div>
    </div>
  );
};

export const ResourceDirectory: FC<PropsWithChildren<DirectoryProps & { actions?: ReactNode }>> = ({
  children,
  directoryId,
  itemId,
  actions
}) => {
  const [folder] = useChildren(itemId);
  const tree = useItemTree(directoryId, itemId);
  if (!folder || !tree) throw new Error('Missing folder or tree');

  // Look through the known directory structure and sort the items into folders and assets
  const originalItems = tree.items?.sort(sortItem);
  const knownFolders = originalItems?.filter(child => child.type === 'folder');
  const knownAssets = originalItems?.filter(child => child.type === 'file');

  const folders = folder.value?.value
    .filter(child => {
      const isKnownFolder = knownFolders?.some(f => f.id === child.id);
      if (isKnownFolder) return true;
      return false;
    })
    .sort(sortItem);

  const assets = folder.value?.value
    .filter(child => {
      const isKnownAsset = knownAssets?.some(f => f.id === child.id);
      if (isKnownAsset) return true;
      return false;
    })
    .sort(sortItem);

  const allIds = [...folders.map(folder => folder.id!), ...assets.map(asset => asset.id!)];
  const hasFolders = folders && folders.length > 0;
  const hasAssets = assets && assets.length > 0;

  return (
    <div className="flex w-full flex-col h-full overflow-y-hidden">
      <div className="pb-5 border-b border-secondary-std flex px-7 justify-between bg-white bg-opacity-80">
        {children}
        <div className="flex gap-3">
          {actions}
          <SelectAllButton ids={allIds} />
        </div>
      </div>
      <div className="p-2 gap-2 flex-1 overflow-y-auto">
        {!hasFolders && !hasAssets && (
          <Card className="py-5 px-10 flex flex-col items-center gap-5 w-2/3 mx-auto max-w-4xl text-center mt-10">
            <p className="font-semibold">This folder is empty.</p>
          </Card>
        )}
        {hasFolders && (
          <FolderList>
            {folders.map(folder => {
              if (!folder.id || !folder.name || !folder.folder) throw new Error('Missing folder id, name, or folder');
              return (
                <Folder
                  key={folder.id}
                  directoryId={directoryId}
                  id={folder.id}
                  name={folder.name}
                  count={folder.folder.childCount ?? 0}
                />
              );
            })}
          </FolderList>
        )}
        {hasAssets && (
          <ResourceList>
            {assets.map(asset => {
              const id = asset.id;
              if (!id || !asset.name) throw new Error('Missing asset id or name');

              return (
                <SelectionContainer key={id} id={id}>
                  <div>
                    <Resource key={asset.id} name={asset.name} id={id}>
                      {asset.folder ? (
                        <ResourceThumbnail id={id} alt={asset.name} />
                      ) : (
                        <Thumbnail set={asset.thumbnails?.[0]} id={id} alt={asset.name} />
                      )}
                    </Resource>
                  </div>
                </SelectionContainer>
              );
            })}
          </ResourceList>
        )}
      </div>
    </div>
  );
};

const CreateFolderFormFields = z.object({
  name: z.string()
});

type CreateFolderFormFields = z.infer<typeof CreateFolderFormFields>;

const CreateFolderForm: FC<ModalProps<{ parentFolderId: string }>> = props => {
  const parentFolderId = props.props?.parentFolderId;
  if (!parentFolderId) throw new Error('Missing parentFolderId');
  const methods = useForm<CreateFolderFormFields>({ resolver: zodResolver(CreateFolderFormFields) });

  const createFolder = useCreateFolder(parentFolderId);

  const onSubmit = async (data: CreateFolderFormFields) => {
    await createFolder.mutateAsync({ name: data.name });
    props.close();
  };

  return (
    <Form className="flex flex-col gap-3" methods={methods} onSubmit={onSubmit}>
      <TextField label="Name" id="name" placeholder="Type folder name" />
      <Button loading={createFolder.isLoading} type="submit">
        Create folder
      </Button>
    </Form>
  );
};

export const CreateFolderModal: FC<ModalProps<{ parentFolderId: string }>> = props => {
  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="p-8 flex flex-col gap-4 rounded-lg overflow-clip relative">
        <Modal.Controls {...props}>
          <h2 className="text-2xl font-semibold">Create folder</h2>
        </Modal.Controls>
        <CreateFolderForm {...props} />
      </Card>
    </Modal>
  );
};

export const ResourceDirectoryPage: FC = () => {
  const { directoryId, itemId } = useParams<{ directoryId: string; itemId: string }>();
  if (!directoryId || !itemId) throw new Error('Missing directoryId or itemId');

  const [, setSize] = useSize();
  const prefix = useExplorer(state => state.prefix);

  const item = useItem(directoryId, itemId);
  const tree = useItemTree(directoryId, itemId);

  if (!item || !tree) throw new Error('Page: Missing item or tree');

  const uploadFiles = useUploadFiles();

  const invalidateChildren = useInvalidateChildren(itemId);
  const invalidateList = useInvalidateList();

  const onUploadFiles = useCallback(
    async (files: File[]) => {
      toast(`Starting upload of ${files.length} files.`);
      await uploadFiles(files, { folder: itemId });
      toast(`Upload of ${files.length} files complete.`);
      await invalidateList();
      await invalidateChildren();
    },
    [invalidateChildren, invalidateList, itemId, uploadFiles]
  );
  const dropProps = useDropzone({ onDrop: onUploadFiles, noClick: true });
  const createFolderModalProps = useModal<{ parentFolderId: string }>(false);

  const role = useRole();

  const isAdmin = role.type === RoleType.Admin;

  const mainContent = (
    <Suspense
      fallback={
        <DirectoryPlaceholder
          directoryId={directoryId}
          itemId={itemId}
          actions={
            <>
              {isAdmin && (
                <>
                  <Button>
                    <SiUpload className="text-xl" />
                    Upload files
                  </Button>
                  <Button>
                    <SiPlus className="text-xl" />
                    Create folder
                  </Button>
                </>
              )}
            </>
          }
        >
          <h1 className="text-3xl font-bold mb-2 md:mb-0 md:mr-5 pl-2">{item.name}</h1>
        </DirectoryPlaceholder>
      }
    >
      <ResourceDirectory
        key={`${directoryId}/${itemId}`}
        directoryId={directoryId}
        itemId={itemId}
        actions={
          <>
            <CreateFolderModal {...createFolderModalProps} />

            {isAdmin && (
              <>
                <Button onClick={() => dropProps.inputRef.current?.click()}>
                  <SiUpload className="text-xl" />
                  Upload files
                </Button>
                <Button onClick={() => createFolderModalProps.open({ parentFolderId: itemId })}>
                  <SiPlus className="text-xl" />
                  Create folder
                </Button>
              </>
            )}
          </>
        }
      >
        <h1 className="text-3xl font-bold mb-2 md:mb-0 md:mr-5">{item.name}</h1>
      </ResourceDirectory>
    </Suspense>
  );

  return (
    <div className="text-primary-std w-full relative flex-1 flex flex-col overflow-y-hidden">
      <nav className="flex items-center justify-between px-5 py-5 bg-white bg-opacity-80">
        <div className="flex h-8 items-center gap-2">
          {item.path.length > 1 && (
            <Link
              to={`${prefix}/${directoryId}/${item.path.at(-2)}`}
              className="bg-secondary-std border-primary-std flex h-8 w-8 items-center justify-center rounded-lg border"
            >
              <svg width="6" height="9" viewBox="0 0 6 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5 8L1 4.5L5 1"
                  stroke="#444444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          )}
        </div>
        <div className="flex h-8 items-center gap-2">
          <button
            onClick={() => setSize('small')}
            className="bg-secondary-std border-primary-std flex h-8 w-8 items-center justify-center rounded-lg border"
          >
            <SiGrid />
          </button>
          <button
            onClick={() => setSize('medium')}
            className="bg-secondary-std border-primary-std flex h-8 w-8 items-center justify-center rounded-lg border"
          >
            <SiGrid className="text-xl" />
          </button>
          <button
            onClick={() => setSize('large')}
            className="bg-secondary-std border-primary-std flex h-8 w-8 items-center justify-center rounded-lg border"
          >
            <SiGrid className="text-2xl" />
          </button>
        </div>
      </nav>
      <div className="bg-white bg-opacity-50 h-full overflow-y-hidden">
        {isAdmin ? (
          <DropContainer
            message={
              <p>
                Drop files anywhere to upload them to <span className="font-semibold italic">{item.name}</span>
              </p>
            }
            {...dropProps}
          >
            {mainContent}
          </DropContainer>
        ) : (
          <div className="h-full">{mainContent}</div>
        )}
      </div>
    </div>
  );
};
